import { useCallback } from 'react'
import { toast } from 'sonner'
import type { ComboVariation, Deal, PropostaItem } from '@/types'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { LayoutConfig } from '@/utils/dealCalculations'
import {
  downloadStudioPresentationPdf,
  type StudioPresentationPdfSession,
} from '@/utils/downloadStudioPresentationPdf'
import { normalizeItemSetupEntry } from '@/utils/recurringSetupEntry'
import { buildFinalLayoutForSave } from './proposalStudioSave'
import type { StudioActionDeps } from './proposalStudioActionTypes'

export function useProposalStudioPersistActions(
  deps: StudioActionDeps & {
    deal: Deal
    onSave?: (items: PropostaItem[], layout: LayoutConfig, comboVariations?: ComboVariation[]) => void
    onClose: () => void
  },
) {
  const {
    deal,
    proposalItems,
    comboVariations,
    activeIndex,
    activePlan,
    layout,
    onSave,
    onClose,
    setSaving,
    setSavingAsTemplate,
  } = deps

  const handleSave = useCallback(async () => {
    if (proposalItems.length === 0) {
      toast.error('Adicione pelo menos um item à proposta')
      return
    }
    setSaving(true)
    try {
      const items = proposalItems.map(normalizeItemSetupEntry)
      const activeP =
        comboVariations.length > 0 ? activePlan : items[activeIndex] || items[0]
      const finalLayout = buildFinalLayoutForSave(layout, activeP)
      onSave?.(items, finalLayout, comboVariations)
      onClose()
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar proposta'))
    } finally {
      setSaving(false)
    }
  }, [
    proposalItems,
    comboVariations,
    activeIndex,
    activePlan,
    layout,
    onSave,
    onClose,
    setSaving,
  ])

  const handleExportPdf = useCallback(async (session: StudioPresentationPdfSession) => {
    if (proposalItems.length === 0) {
      toast.error('Adicione pelo menos um item à proposta')
      return
    }
    setSavingAsTemplate(true)
    const toastId = toast.loading('Gerando apresentação em PDF...')
    try {
      await downloadStudioPresentationPdf(deal.empresaNome || 'Cliente', session)
      toast.success('Apresentação em PDF baixada!', { id: toastId })
    } catch (err: unknown) {
      console.error(err)
      toast.error(getFriendlyError(err, 'Erro ao baixar a apresentação'), { id: toastId })
    } finally {
      session.restore()
      setSavingAsTemplate(false)
    }
  }, [proposalItems.length, deal.empresaNome, setSavingAsTemplate])

  return { handleSave, handleExportPdf }
}
